"use client"

import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell, LabelList,
} from "recharts"
import { type ChartDatum, type ChartDimension } from "./types"
import type { CSSProperties } from "react"

/** A waterfall chart shows how successive positive/negative changes
 *  accumulate to a final total. Each row needs `name` and `change`
 *  (a signed number). A row with `total: true` resets the running sum. */
export interface WaterfallRow {
  name: string
  change: number
  total?: boolean
}

export interface WaterfallChartProps {
  data: WaterfallRow[]
  positiveColor?: string
  negativeColor?: string
  totalColor?: string
  showLegend?: boolean
  showGrid?: boolean
  showTooltip?: boolean
  showValueLabels?: boolean
  xLabel?: string
  yLabel?: string
  className?: string
  style?: CSSProperties
  height?: ChartDimension
  width?: ChartDimension
}

export function WaterfallChart({
  data,
  positiveColor = "#10b981",
  negativeColor = "#ef4444",
  totalColor = "#3b82f6",
  showLegend = true, showGrid = true, showTooltip = true, showValueLabels,
  xLabel, yLabel, className, style, height = "100%", width = "100%",
}: WaterfallChartProps) {
  // Compute running totals so each bar can be drawn as an invisible base
  // (`pad`) plus the visible delta (`value`). For a "total" row the base
  // is 0 and the visible value is the running sum at that point.
  let running = 0
  const rows: ChartDatum[] = data.map(r => {
    if (r.total) {
      const finalValue = running
      return { name: r.name, pad: 0, value: finalValue, color: totalColor, _kind: "total" }
    }
    const start = running
    running += r.change
    if (r.change >= 0) {
      return { name: r.name, pad: start, value: r.change, color: positiveColor, _kind: "pos" }
    } else {
      return { name: r.name, pad: start + r.change, value: -r.change, color: negativeColor, _kind: "neg" }
    }
  })

  return (
    <ResponsiveContainer width={width} height={height} className={className} style={style}>
      <BarChart data={rows} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
        {showGrid && <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.18} />}
        <XAxis dataKey="name" tick={{ fontSize: 10.5 }} interval={0} angle={-15} dy={6} height={50} label={xLabel ? { value: xLabel, position: "insideBottom", offset: -4 } : undefined} />
        <YAxis tick={{ fontSize: 10.5 }} label={yLabel ? { value: yLabel, angle: -90, position: "insideLeft" } : undefined} />
        {showTooltip && <Tooltip cursor={{ fill: "hsl(var(--foreground) / 0.06)", stroke: "hsl(var(--muted-foreground))", strokeOpacity: 0.35 }} contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 6, fontSize: 11, color: "hsl(var(--card-foreground))" }} />}
        {showLegend && <Legend wrapperStyle={{ fontSize: 11 }} />}
        {/* Invisible padding bar so the visible bar floats at the right baseline. */}
        <Bar dataKey="pad" stackId="w" fill="transparent" isAnimationActive={false} legendType="none" />
        <Bar dataKey="value" stackId="w" radius={[3, 3, 0, 0]} isAnimationActive={false}>
          {rows.map((row, i) => <Cell key={i} fill={String(row.color)} />)}
          {showValueLabels && <LabelList dataKey="value" position="top" style={{ fontSize: 10 }} />}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  )
}
